// ============================================================================
// SubReplacementCard — propose_sub_replacement. Inserts into
// sub_replacement_requests (status=Pending). Starts the formal workflow;
// does NOT touch service_assignments.
// 2026-05-21 Cowork: AM Co-pilot v2 action expansion.
// ============================================================================

import { useMemo, useState } from 'react';
import { Repeat, AlertTriangle } from 'lucide-react';
import type { CopilotProposalBase } from '../../../shared/types';
import { CardShell } from './card-shell';
import { FacilityCombobox } from './facility-combobox';
import { useSubChangeReasons } from '../../sub-replacement/sub-change-reasons';

import { optionsFor } from './card-options';

// Urgency is the database's own list (sub_replacement_requests_urgency_check), sent with the draft.

interface FacilityOpt {
  facility_id: string;
  facility_name: string;
}

interface SubReplacementCardProps {
  proposal: CopilotProposalBase;
  facilities: FacilityOpt[];
  isCommitting: boolean;
  onDoIt: (edited_input: Record<string, unknown>, was_edited: boolean) => void;
  onDiscard: () => void;
}

export function SubReplacementCard({
  proposal,
  facilities,
  isCommitting,
  onDoIt,
  onDiscard,
}: SubReplacementCardProps) {
  const initial = proposal.input as {
    facility_id?: string;
    outgoing_assignment_id?: string;
    reason?: string;
    reason_code?: string | null;
    urgency?: string;
    hold_pay?: boolean;
    notes?: string | null;
  };

  const [facility_id, setFacilityId] = useState(initial.facility_id ?? '');
  const [outgoingAssignmentId, setOutgoingAssignmentId] = useState(
    initial.outgoing_assignment_id ?? '',
  );
  const [reason, setReason] = useState(initial.reason ?? '');
  // Why, from the list (#667). The copilot proposes one; the AM confirms it.
  const { offered: reasonOptions, error: reasonsError } = useSubChangeReasons();
  const [reasonCode, setReasonCode] = useState(initial.reason_code ?? '');
  const reasonCodeOk = reasonOptions.some((r) => r.reason_code === reasonCode);
  const [urgency, setUrgency] = useState(initial.urgency ?? 'Soon');
  const urgencies = optionsFor(proposal.input, 'urgency', initial.urgency);
  const [holdPay, setHoldPay] = useState(initial.hold_pay ?? false);
  const [notes, setNotes] = useState(initial.notes ?? '');

  const wasEdited = useMemo(() => {
    return (
      facility_id !== (initial.facility_id ?? '') ||
      outgoingAssignmentId !== (initial.outgoing_assignment_id ?? '') ||
      reason !== (initial.reason ?? '') ||
      reasonCode !== (initial.reason_code ?? '') ||
      urgency !== (initial.urgency ?? 'Soon') ||
      holdPay !== (initial.hold_pay ?? false) ||
      (notes || null) !== (initial.notes ?? null)
    );
  }, [facility_id, outgoingAssignmentId, reason, reasonCode, urgency, holdPay, notes, initial]);

  const handleDoIt = () => {
    onDoIt(
      {
        facility_id,
        outgoing_assignment_id: outgoingAssignmentId,
        reason,
        reason_code: reasonCode,
        urgency,
        hold_pay: holdPay,
        notes: notes || null,
      },
      wasEdited,
    );
  };

  return (
    <CardShell
      title="Sub Replacement"
      icon={<Repeat className="h-4 w-4" />}
      accentClass="border-st-danger-border-strong"
      status={proposal.status}
      committedLabel={proposal.target_row_id ? `Saved as ${proposal.target_row_id}` : 'Saved'}
      isCommitting={isCommitting}
      doItDisabled={!reasonCodeOk}
      doItDisabledReason={
        reasonsError
          ? `The list of reasons did not load (${reasonsError}). Reload and try again.`
          : 'Pick why this sub is changing.'
      }
      onDoIt={handleDoIt}
      onDiscard={onDiscard}
    >
      <div className="flex items-start gap-2 rounded-md border border-st-danger-border bg-st-danger-bg px-3 py-2 text-xs text-st-danger-fg-strong">
        <AlertTriangle className="h-4 w-4 flex-shrink-0" />
        <span>This starts the formal sub-replacement workflow. COO approval required before any cutover — no service_assignment change happens yet.</span>
      </div>
      <Field label="Facility">
        <FacilityCombobox value={facility_id} options={facilities} onChange={setFacilityId} />
      </Field>
      <Field label="Outgoing assignment (ASN-XXXX)">
        <input
          value={outgoingAssignmentId}
          onChange={(e) => setOutgoingAssignmentId(e.target.value.toUpperCase())}
          className={inputClass}
          placeholder="ASN-0001"
        />
      </Field>
      <Field label="Why is this sub changing?">
        <select
          value={reasonCode}
          onChange={(e) => setReasonCode(e.target.value)}
          className={inputClass}
        >
          <option value="">Pick one…</option>
          {reasonOptions.map((r) => (
            <option key={r.reason_code} value={r.reason_code}>
              {r.label}
            </option>
          ))}
        </select>
      </Field>
      <Field label="What happened, in words (≥10 chars, internal-only)">
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={3}
          className={`${inputClass} resize-none`}
        />
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Urgency">
          <div className="flex gap-1">
            {urgencies.map((u) => (
              <button
                key={u}
                type="button"
                onClick={() => setUrgency(u)}
                className={`flex-1 rounded-md px-2 py-1 text-xs font-medium ${
                  urgency === u
                    ? u === 'Emergency'
                      ? 'bg-st-danger-solid text-st-solid-ink'
                      : u === 'Soon'
                      ? 'bg-st-warn-solid text-st-solid-ink'
                      : 'bg-tcs-inverse-soft text-tcs-inverse-ink'
                    : 'bg-tcs-surface text-tcs-text-secondary hover:bg-tcs-surface-strong'
                }`}
              >
                {u}
              </button>
            ))}
          </div>
        </Field>
        <Field label="Final payment per the Subcontractor Agreement">
          <label className="flex items-center gap-2 text-sm text-tcs-text-secondary">
            <input
              type="checkbox"
              checked={holdPay}
              onChange={(e) => setHoldPay(e.target.checked)}
            />
            Hold until the handover is complete
          </label>
        </Field>
      </div>
      <Field label="Notes">
        <textarea
          value={notes ?? ''}
          onChange={(e) => setNotes(e.target.value)}
          rows={2}
          className={`${inputClass} resize-none`}
        />
      </Field>
    </CardShell>
  );
}

const inputClass =
  'w-full rounded-md border border-tcs-border bg-tcs-field px-2 py-1.5 text-sm text-tcs-text focus:border-tcs-text-muted focus:outline-none focus:ring-1 focus:ring-tcs-text-muted';

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium uppercase tracking-wide text-tcs-text-muted">{label}</span>
      {children}
    </label>
  );
}
